import { useCallback, useEffect, useRef } from 'react';
import { useUser } from '../context/userContext';
import { mergePending, readPending, clearPending, hasPending } from '../persistence/backgroundSaves';
import { DEFAULT_ANSWERS } from '../context/defaultAnswers';
import { getProgress, Section } from '../persistence/localProgress';
import { appendTime, mergeTimes, readTimes } from '../persistence/answerTimes';

type Timer = ReturnType<typeof setTimeout> | null;

export function useBackgroundAnswerSave() {
  const { company, saveUserAnswersBackground, answers, answerTimes: serverTimes } = useUser();
  const companyId = company?.id;
  const userId = localStorage.getItem('id') || undefined;

  const flushTimerRef = useRef<Timer>(null);
  const backoffRef = useRef<number>(500); // start with 500ms
  const inFlightRef = useRef<Promise<unknown> | null>(null);
  const needsAnotherFlushRef = useRef<boolean>(false);

  const flush = useCallback(() => {
    if (!companyId || !userId) return;
    const payload = readPending(companyId, userId);
    if (Object.keys(payload).length === 0) return;

    // Build full answers: defaults -> server answers -> local progress per section -> pending overlay
    // This ensures we always send a complete, up-to-date set including nulls
    const mergeLocalProgress = (section: Section) => getProgress(companyId, section).answers || {};
    const localPersonality = mergeLocalProgress('personality');
    const localInterests = mergeLocalProgress('interests');
    const localValues = mergeLocalProgress('values');

    const fullAnswers = {
      ...DEFAULT_ANSWERS,
      ...(answers as Record<string, boolean | null>),
      ...localPersonality,
      ...localInterests,
      ...localValues,
      ...payload,
    } as Record<string, boolean | null>;

    // Coalesce concurrent flushes: if one is in flight, mark to run again and exit
    if (inFlightRef.current) {
      needsAnotherFlushRef.current = true;
      return;
    }

    // Merge answerTimes: server -> local (dedup)
    const localTimes = readTimes(companyId, userId);
    const serverAsMap = (serverTimes || {}) as unknown as Record<string, string[] | undefined>;
    const toTimesMap = (src: Record<string, string[] | undefined>) => {
      const result: Record<string, string[]> = {};
      Object.keys(src).forEach((k) => {
        const v = src[k];
        result[k] = v || [];
      });
      return result;
    };
    const fullTimes = mergeTimes(toTimesMap(serverAsMap), localTimes);

    inFlightRef.current = saveUserAnswersBackground(fullAnswers, fullTimes)
      .then(() => {
        clearPending(companyId, userId);
        backoffRef.current = 500;
      })
      .catch(() => {
        const delay = Math.min(backoffRef.current * 2, 8000);
        backoffRef.current = delay;
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
        flushTimerRef.current = setTimeout(() => flush(), delay);
      })
      .finally(() => {
        inFlightRef.current = null;
        if (needsAnotherFlushRef.current) {
          needsAnotherFlushRef.current = false;
          if (hasPending(companyId, userId)) flush();
        }
      });
  }, [companyId, userId, saveUserAnswersBackground, answers, serverTimes]);

  const saveAnswer = useCallback(
    (questionId: string, value: boolean | null) => {
      if (!companyId || !userId) return;
      mergePending(companyId, userId, { [questionId]: value });
      // record timestamp locally
      appendTime(companyId, userId, questionId, new Date().toISOString());
      // Flush immediately to avoid unmount-loss and stuck pending state
      flush();
    },
    [companyId, userId, flush],
  );

  useEffect(() => {
    if (!companyId || !userId) return;
    if (hasPending(companyId, userId)) {
      // Try to flush immediately if something is pending
      flush();
    }
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, [companyId, userId, flush]);

  return { saveAnswer, flushPending: flush };
}
